import type { ReactNode } from 'react';
import { TrendUp, TrendDown, Minus } from '@phosphor-icons/react';

interface TrendProps {
  value: number;
  label?: string;
}

type TrendDirection = 'higher-is-better' | 'higher-is-worse' | 'neutral';

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon?: ReactNode;
  trend?: TrendProps;
  trendDirection?: TrendDirection;
  /** Window suffix appended to the label, e.g. "24h" → "(24H)". */
  windowLabel?: string;
}

function resolveTrendColor(value: number, direction: TrendDirection): string {
  if (direction === 'neutral') return 'text-muted';
  if (value === 0) return 'text-muted';
  if (direction === 'higher-is-better') {
    return value > 0 ? 'text-green-700' : 'text-red-700';
  }
  // higher-is-worse
  return value > 0 ? 'text-red-700' : 'text-green-700';
}

export function StatCard({
  label,
  value,
  sub,
  icon,
  trend,
  trendDirection = 'higher-is-better',
  windowLabel,
}: StatCardProps) {
  const trendColor = !trend
    ? ''
    : resolveTrendColor(trend.value, trendDirection);

  const TrendIcon =
    !trend || trend.value === 0
      ? Minus
      : trend.value > 0
        ? TrendUp
        : TrendDown;

  const trendVsLabel =
    trend?.label ?? (windowLabel ? `vs prev ${windowLabel}` : undefined);

  return (
    <div className="bg-surface border border-border rounded-xl p-5 shadow-(--c-shadow-sm)">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-muted uppercase tracking-wide">
          {label}
          {windowLabel && (
            <span className="ml-1 text-muted/70 font-normal">
              ({windowLabel})
            </span>
          )}
        </p>
        {icon && (
          <span className="w-7 h-7 rounded-lg bg-accent-faint flex items-center justify-center text-muted">
            {icon}
          </span>
        )}
      </div>
      <p className="text-[28px] font-bold tracking-tight text-text leading-none mb-1">
        {value}
      </p>
      {sub && <p className="text-xs text-muted mt-1">{sub}</p>}
      {trend && (
        <div
          className={`flex items-center gap-1 mt-2 text-xs font-medium ${trendColor}`}
        >
          <TrendIcon size={12} weight="bold" />
          <span>{Math.abs(trend.value)}%</span>
          {trendVsLabel && (
            <span className="text-muted font-normal">&nbsp;{trendVsLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}
